import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import {
  createAccountingAccount,
  getAccountingAccounts,
  getAccountingFormData,
  updateAccountingAccount,
} from "../../services/accountingService"



const formatMoney = (value, currency = "USD") =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))


const formatDate = (value) => {
  if (!value) return "-"
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat("fr-FR").format(date)
}


const extractErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail

  if (typeof detail === "string") {
    return detail
  }

  if (detail && typeof detail === "object") {
    return detail.message || detail.detail || fallback
  }

  return error?.message || fallback
}



const emptyForm = {
  code: "",
  name: "",
  account_type: "ASSET",
  parent_id: "",
  currency: "",
  is_control_account: false,
  allow_manual_entry: true,
  is_active: true,
  notes: "",
}


export default function ChartOfAccountsPage() {
  const [rows, setRows] = useState([])
  const [formData, setFormData] = useState({ account_types: [], accounts: [] })
  const [search, setSearch] = useState("")
  const [accountType, setAccountType] = useState("all")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)

  const loadData = useCallback(async () => {
    setLoading(true)
    setError("")

    try {
      const [accountsResult, formResult] = await Promise.all([
        getAccountingAccounts({
          search,
          account_type: accountType,
        }),
        getAccountingFormData(),
      ])

      setRows(accountsResult?.items || [])
      setFormData(formResult || { account_types: [], accounts: [] })
    } catch (err) {
      setError(
        extractErrorMessage(
          err,
          "Impossible de charger le plan comptable."
        )
      )
    } finally {
      setLoading(false)
    }
  }, [search, accountType])

  useEffect(() => {
    loadData()
  }, [loadData])

  const openCreate = () => {
    setEditing(null)
    setForm({ ...emptyForm })
    setDialogOpen(true)
  }

  const openEdit = (row) => {
    setEditing(row)
    setForm({
      code: row.code || "",
      name: row.name || "",
      account_type: row.account_type || "ASSET",
      parent_id: row.parent_id || "",
      currency: row.currency || "",
      is_control_account: Boolean(row.is_control_account),
      allow_manual_entry: Boolean(row.allow_manual_entry),
      is_active: Boolean(row.is_active),
      notes: row.notes || "",
    })
    setDialogOpen(true)
  }

  const save = async () => {
    try {
      const payload = {
        ...form,
        parent_id: form.parent_id ? Number(form.parent_id) : null,
        currency: form.currency || null,
        notes: form.notes || null,
      }

      if (editing) {
        await updateAccountingAccount(editing.id, payload)
      } else {
        await createAccountingAccount(payload)
      }

      setDialogOpen(false)
      await loadData()
    } catch (err) {
      setError(
        extractErrorMessage(
          err,
          "Impossible d'enregistrer le compte."
        )
      )
    }
  }

  const columns = useMemo(
    () => [
      { field: "code", headerName: "Code", minWidth: 110 },
      { field: "name", headerName: "Compte", minWidth: 220, flex: 1 },
      { field: "account_type", headerName: "Type", minWidth: 140 },
      {
        field: "parent_name",
        headerName: "Parent",
        minWidth: 180,
        valueGetter: (_, row) => row.parent_name || "-",
      },
      {
        field: "raw_balance",
        headerName: "Solde brut",
        minWidth: 150,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatMoney(value),
      },
      {
        field: "is_active",
        headerName: "Actif",
        minWidth: 90,
        valueGetter: (_, row) => (row.is_active ? "Oui" : "Non"),
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 120,
        sortable: false,
        renderCell: (params) => (
          <Button size="small" onClick={() => openEdit(params.row)}>
            Modifier
          </Button>
        ),
      },
    ],
    []
  )

  return (
    <MainLayout>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={900}>
              Plan comptable
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Comptes utilisés dans les écritures automatiques et manuelles.
            </Typography>
          </Box>

          <Button variant="contained" onClick={openCreate}>
            Nouveau compte
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
              gap: 1.5,
            }}
          >
            <TextField
              size="small"
              label="Rechercher"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />

            <FormControl size="small">
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={accountType}
                onChange={(event) => setAccountType(event.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                {formData.account_types?.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box sx={{ height: 600 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
              }}
              sx={{ border: 0 }}
            />
          </Box>
        </Paper>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {editing ? "Modifier le compte" : "Nouveau compte"}
          </DialogTitle>

          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="Code *"
                value={form.code}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    code: event.target.value,
                  }))
                }
              />

              <TextField
                label="Nom *"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
              />

              <FormControl>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={form.account_type}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      account_type: event.target.value,
                    }))
                  }
                >
                  {formData.account_types?.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <InputLabel>Compte parent</InputLabel>
                <Select
                  label="Compte parent"
                  value={form.parent_id}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      parent_id: event.target.value,
                    }))
                  }
                >
                  <MenuItem value="">Aucun</MenuItem>
                  {formData.accounts
                    ?.filter((item) => item.id !== editing?.id)
                    .map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {`${item.code} — ${item.name}`}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <TextField
                label="Devise spécifique"
                value={form.currency}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    currency: event.target.value,
                  }))
                }
              />

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography>Compte actif</Typography>
                <Switch
                  checked={form.is_active}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      is_active: event.target.checked,
                    }))
                  }
                />
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography>Autoriser les écritures manuelles</Typography>
                <Switch
                  checked={form.allow_manual_entry}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      allow_manual_entry: event.target.checked,
                    }))
                  }
                />
              </Stack>

              <TextField
                label="Notes"
                multiline
                minRows={2}
                value={form.notes}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    notes: event.target.value,
                  }))
                }
              />
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>

            <Button variant="contained" onClick={save}>
              Enregistrer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  )
}
